import React from 'react';
import { Link } from 'react-router-dom';

import { PageTransition, Reveal } from '../components/PageTransition';
import { useSiteChat } from '../contexts/ChatContext';
import Section from '../components/Section';
import Seo from '../components/Seo';
import avatarImage from '../assets/avator_1.jpeg';
import {
  REACT_APP_OWNER,
  REACT_APP_SITE_URL,
  REACT_APP_SOCIAL_GITHUB,
  REACT_APP_SOCIAL_LINKEDIN,
  REACT_APP_SOCIAL_TWITTER,
} from '../env';

/**
 * Skills grouped by the job they do. A flat list of two dozen tags tells the
 * reader nothing; these groups say where the depth is.
 */
const skillGroups = [
  { label: 'Languages', items: ['JavaScript', 'TypeScript', 'Java'] },
  { label: 'Interfaces', items: ['React', 'React Native', 'USSD', 'Offline-first'] },
  { label: 'Services', items: ['Node.js', 'REST', 'GraphQL', 'Microservices'] },
  { label: 'Data', items: ['MongoDB', 'PostgreSQL', 'Redis'] },
  { label: 'Infrastructure', items: ['AWS', 'Docker', 'Kubernetes', 'Linux'] },
  { label: 'Leading', items: ['Cross-functional teams', 'Start-up strategy', 'Agile'] },
];

const experience = [
  {
    title: 'Senior Software Engineer',
    company: 'One Acre Fund',
    period: '2021 —',
    location: 'Kigali, Rwanda',
    description: 'Mobile and USSD solutions for smallholder farmers across Africa.',
    achievements: [
      'Led the award-winning Tupande mobile app, an e-commerce platform for smallholder farmers',
      'Directed a cross-functional team shipping USSD services in multiple countries',
      'Designed a plug-and-play USSD framework for rapid deployment of configurable modules',
      'Piloted fine-tuning LLaMA on multilingual call centre data',
      'Currently leading an offline-first tool for field agents in underserved regions',
    ],
  },
  {
    title: 'Co-Founder',
    company: 'BuniAI',
    period: '2024 —',
    description: 'Conversational logic engineering on a collaborative, AI-driven canvas.',
    achievements: [
      'Set strategy for a platform turning business requirements into production USSD and chatbot workflows',
      'Cut development cycles from weeks to days',
      'Pushing the field from "no-code builders" toward high-performance workflow engineering',
    ],
  },
  {
    title: 'Software Engineer',
    company: 'Banjo / safeXai',
    period: '2019 — 2021',
    location: 'South Jordan, Utah',
    description: 'Enterprise software with a focus on security and scale.',
  },
  {
    title: 'Lead Software Engineer',
    company: 'OptiMine Software',
    period: '2014 — 2019',
    location: 'Minneapolis, Minnesota',
    description: 'Five years building marketing analytics and optimisation software.',
    achievements: [
      'Promoted to Lead Software Engineer in 2017',
      'Architected and delivered enterprise analytics platforms',
      'Mentored the development team and set engineering practice',
    ],
  },
];

const education = [
  { school: 'University of Wisconsin–Eau Claire', degree: 'BSc, Computer Science', period: '2010 — 2013' },
  { school: 'Egerton University', degree: 'BSc, Applied Computer Science', period: '2009' },
  { school: 'Egerton University', degree: 'Diploma, Computer Science', period: '2006 — 2008' },
];

const contact = [
  { label: 'LinkedIn', href: REACT_APP_SOCIAL_LINKEDIN || 'https://www.linkedin.com/in/dmnyabuti' },
  { label: 'GitHub', href: REACT_APP_SOCIAL_GITHUB },
  { label: 'Twitter', href: REACT_APP_SOCIAL_TWITTER },
].filter((item) => Boolean(item.href));

function Row({ left, children }) {
  return (
    <div className="grid gap-3 border-b border-rule py-8 grid-cols-1 sm:grid-cols-[8.5rem_minmax(0,1fr)] sm:gap-8">
      <div className="label pt-1">{left}</div>
      <div>{children}</div>
    </div>
  );
}

function About() {
  const { openChat } = useSiteChat();
  const siteUrl = REACT_APP_SITE_URL || 'https://dennisnyabuti.com';
  const profileSchema = {
    '@context': 'https://schema.org',
    '@type': 'Person',
    name: REACT_APP_OWNER || 'Dennis M. Nyabuti',
    url: `${siteUrl}/about`,
    jobTitle: 'Senior Software Engineer',
    worksFor: { '@type': 'Organization', name: 'One Acre Fund' },
    homeLocation: { '@type': 'Place', name: 'Kigali, Rwanda' },
    alumniOf: education.map((item) => ({ '@type': 'EducationalOrganization', name: item.school })),
    knowsAbout: skillGroups.flatMap((group) => group.items),
  };

  return (
    <>
      <Seo
        title="About | Dennis M. Nyabuti"
        description="Ten years of software engineering across two continents — USSD, offline-first mobile, cloud architecture and AI-enabled workflows."
        path="/about"
        image={avatarImage}
        keywords="about Dennis Nyabuti, senior software engineer, USSD, offline-first, Kigali, engineering leadership"
        schema={profileSchema}
      />

      <PageTransition>
        <div className="shell pb-8 pt-12 sm:pt-16">
          {/* Header */}
          <header className="grid items-start gap-10 border-b border-rule pb-12 sm:grid-cols-[1fr_auto] sm:gap-16">
            <div>
              <p className="label">About</p>
              <h1 className="mt-5 max-w-2xl text-title">
                Ten years, two continents,
                <br />
                one stubborn problem.
              </h1>
              <p className="mt-6 max-w-prose text-deck text-muted-foreground">
                I build software for people with patchy networks and cheap phones.
              </p>
            </div>
            <img
              alt="Dennis Nyabuti"
              src={avatarImage}
              width={176}
              height={220}
              className="order-first h-[220px] w-[176px] rounded-sm border border-rule object-cover sm:order-none"
            />
          </header>

          <div className="space-y-20 pt-16">
            {/* Bio */}
            <Reveal>
              <div className="grid gap-8 grid-cols-1 sm:grid-cols-[8.5rem_minmax(0,1fr)] sm:gap-8">
                <p className="label pt-2">Background</p>
                <div className="max-w-prose space-y-6 font-display text-[1.1875rem] leading-[1.75]">
                  <p>
                    I am a senior software engineer with more than a decade spent building things that
                    have to hold up under real constraints. I live in Kigali and lead mobile and USSD
                    development at One Acre Fund, where the users are smallholder farmers and the design
                    brief is a feature phone with two bars of signal.
                  </p>
                  <p>
                    That work has produced the award-winning Tupande mobile app and a USSD framework now
                    running in several countries. Alongside it I co-founded{' '}
                    <a href="https://buniai.com" target="_blank" rel="noopener noreferrer" className="text-signal">
                      BuniAI
                    </a>
                    , which turns business requirements into production conversational workflows and has
                    taken our own build cycles from weeks down to days.
                  </p>
                  <p>
                    Before Rwanda there was Utah and, before that, five years in Minneapolis building
                    marketing analytics. The through-line has always been the same: get the hard parts
                    right where the margin for error is thin, and keep the team moving while you do it.
                  </p>
                </div>
              </div>
            </Reveal>

            {/* Experience */}
            <Reveal>
              <Section title="Experience">
                {experience.map((job) => (
                  <Row key={`${job.company}-${job.title}`} left={<>{job.period}{job.location && <span className="mt-1 block normal-case tracking-normal">{job.location}</span>}</>}>
                    <h3 className="font-display text-xl leading-snug">{job.title}</h3>
                    <p className="mt-1 text-sm text-signal">{job.company}</p>
                    <p className="mt-3 max-w-prose text-[0.9375rem] leading-relaxed text-muted-foreground">
                      {job.description}
                    </p>
                    {job.achievements && (
                      <ul className="mt-4 max-w-prose space-y-2 text-[0.9375rem] leading-relaxed text-muted-foreground">
                        {job.achievements.map((achievement) => (
                          <li key={achievement} className="flex gap-3">
                            <span className="mt-2.5 h-px w-3 shrink-0 bg-signal" aria-hidden="true" />
                            <span>{achievement}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </Row>
                ))}
              </Section>
            </Reveal>

            {/* Toolkit */}
            <Reveal>
              <Section title="Toolkit">
                {skillGroups.map((group) => (
                  <Row key={group.label} left={group.label}>
                    <p className="font-mono text-sm leading-relaxed">
                      {group.items.map((item, i) => (
                        <React.Fragment key={item}>
                          {i > 0 && <span className="text-rule"> / </span>}
                          {item}
                        </React.Fragment>
                      ))}
                    </p>
                  </Row>
                ))}
              </Section>
            </Reveal>

            {/* Education */}
            <Reveal>
              <Section title="Education">
                {education.map((item) => (
                  <Row key={`${item.school}-${item.period}`} left={item.period}>
                    <h3 className="font-display text-xl leading-snug">{item.school}</h3>
                    <p className="mt-1 text-sm text-muted-foreground">{item.degree}</p>
                  </Row>
                ))}
              </Section>
            </Reveal>

            {/* Contact */}
            <Reveal>
              <Section id="contact" title="Get in touch">
                <div className="grid gap-8 py-10 grid-cols-1 sm:grid-cols-[8.5rem_minmax(0,1fr)] sm:gap-8">
                  <p className="label pt-2">Kigali, Rwanda</p>
                  <div className="space-y-6">
                    <p className="max-w-prose text-deck">
                      Collaborations, hard infrastructure problems, or a conversation about building for
                      low-connectivity users — all welcome.
                    </p>
                    <ul className="flex flex-wrap gap-x-8 gap-y-3 text-sm">
                      {contact.map((item) => (
                        <li key={item.label}>
                          <a href={item.href} target="_blank" rel="noopener noreferrer" className="link-rule">
                            {item.label}
                          </a>
                        </li>
                      ))}
                      <li>
                        <button type="button" onClick={openChat} className="link-rule">
                          Ask my assistant
                        </button>
                      </li>
                      <li><Link to="/posts" className="link-rule">Read the writing</Link></li>
                    </ul>
                  </div>
                </div>
              </Section>
            </Reveal>
          </div>
        </div>
      </PageTransition>
    </>
  );
}

export default About;
